import { serverEnv } from '@/lib/env'
import type { Engine } from '@/lib/ai/constants'

// ─── Plan definitions ────────────────────────────────────────────────────────

// `dailyFocusPrompts` (R3.15 `backend-spec.md` §2.7 / `PRICING.md:105`) is the daily allowance for
// scoped-Focus prompt collection, pooled per owner account exactly like `prompts` — see
// `getEffectiveDailyFocusAllowance` below, the twin of `getEffectivePromptLimit`.
export const PLANS = {
  starter: { domains: 1, prompts: 50, engines: 3, dailyFocusPrompts: 5 },
  growth: { domains: 3, prompts: 150, engines: 3, dailyFocusPrompts: 15 },
  pro: { domains: 5, prompts: 200, engines: 3, dailyFocusPrompts: 30 },
  agency: { domains: 10, prompts: 400, engines: 3, dailyFocusPrompts: 60 },
} as const

export type PlanKey = keyof typeof PLANS

export const PLAN_PRICES: Record<PlanKey, number> = {
  starter: 89,
  growth: 219,
  pro: 449,
  agency: 699,
}

/**
 * Human-readable plan names. SINGLE SOURCE — the sidebar badge, the shell
 * notice strip and the billing tab must all render the same plan name for the
 * same account; three independent label maps is how they drift.
 */
export const PLAN_LABELS: Record<PlanKey, string> = {
  starter: 'Starter',
  growth: 'Growth',
  pro: 'Pro',
  agency: 'Agency',
}

/**
 * Human-readable name for ANY plan-ish string, including the non-`PlanKey`
 * values the UI carries (`'free'` for no subscription, `'trialing'`), plus a
 * safe fallback for an unknown value.
 *
 * Use this instead of hand-rolling `plan.charAt(0).toUpperCase() + plan.slice(1)`.
 * That idiom existed in five independent places — the sidebar, the dashboard
 * shell, the prompts page, the team tab, and the Polar webhook (which names the
 * plan in the subscription-activated and cancellation EMAILS). Five
 * implementations of one string is how a customer's email comes to disagree
 * with their dashboard.
 */
export function getPlanLabel(plan: string | null | undefined): string {
  if (!plan) return 'Free'
  if (plan in PLAN_LABELS) return PLAN_LABELS[plan as PlanKey]
  if (plan === 'free') return 'Free'
  if (plan === 'trialing') return 'Trial'
  return plan.charAt(0).toUpperCase() + plan.slice(1)
}

/**
 * Days a lapsed (canceled) subscription's data is retained before deletion.
 * Disclosed in Terms, the cancellation email and the T-7 warning, so the
 * dashboard and the billing tab must compute the same date from one constant.
 */
export const LAPSED_RETENTION_DAYS = 90

export const ENGINE_ADDON_PRICES: Record<PlanKey, number> = {
  starter: 20,
  growth: 40,
  pro: 60,
  agency: 100,
}

// The engine lists moved to `@/lib/ai/constants` (which has no imports) so a
// Client Component can read them — this module value-imports `serverEnv`, so
// importing it from the client is an ARC-01 violation, and the alternative was
// screens hand-rolling their own engine array, which is ARC-15's detect.
// Re-exported here so every existing importer is unaffected.
//
// The add-on machinery stays DORMANT for when a paid engine add-on returns:
// ENGINE_ADDON_PRICES, getEngineAddonPrice, getEngineAddonProductId, isEngineAddonProduct,
// createEngineAddonCheckout (src/lib/api/billing.ts) + its route /api/polar/engine-addon-checkout
// (guarded to 404 while ADDON_ENGINES is empty), and the Polar engine-addon webhook handlers.
export { BASE_ENGINES, ADDON_ENGINES, OFFERED_ENGINES } from '@/lib/ai/constants'
// Also imported (not just re-exported) — a re-export does not bind the names
// in this module's own scope, and getEntitledEngines below reads them.
import { BASE_ENGINES, ADDON_ENGINES } from '@/lib/ai/constants'

/**
 * Feature copy only — domain/prompt quotas are a SEPARATE concern, rendered
 * from `getPlanQuotaSummary()` below (design-audit.md row 5.23: the onboarding
 * plan-selector's "After your trial:" line owns the quota figures now, so
 * repeating them here would duplicate the same fact twice on one card).
 *
 * Team-member counts left too, on 2026-08-12: `getPlanQuotaSummary()` now
 * renders seats as its third figure, so a "3 team members" bullet here would
 * restate on the same card what the line above it already says (PRD-13). The
 * mock's feature lists carry no seat bullet either. The claim did not move
 * out of the product, only out of this list.
 */
export const PLAN_FEATURES: Record<PlanKey, string[]> = {
  starter: [
    'Daily monitoring across 3 AI engines (ChatGPT, Perplexity & Google AI Overview)',
    'Growth Plan: Suggestions ranked by reach, Tasks checked live where possible',
    'Sources: the third-party pages AI cites, ranked by reach',
    'Accuracy monitoring (Truth Accuracy Rate)',
    'PDF/CSV export & shared reports',
  ],
  growth: ['Everything in Starter', 'Priority support'],
  pro: ['Everything in Growth'],
  agency: ['Everything in Pro', 'White-label client reports', 'Dedicated support'],
}

/**
 * "After your trial" quota summary — "1 domain · 50 prompts · 3 seats", the
 * mock's three figures (design-audit.md row 5.20).
 *
 * Seats were excluded until 2026-08-12 on the reading that `PLANS` has no seat
 * field and rendering one would be PRD-02. That was half right: `PLANS` indeed
 * has none, but `PLAN_TEAM_LIMITS` does, and it is real config — the block
 * above this file's own `PLAN_FEATURES` already called it "a real,
 * non-fabricated config value" while these figures were being withheld on the
 * grounds that they did not exist. PRD-02 forbids dressing an *inferred* value
 * as a measured one; a plan's seat allowance is neither. It is a stated
 * product limit, exactly like the domain and prompt counts beside it.
 *
 * `null` means unlimited (Agency), which the mock renders as "unlimited" —
 * not a number, and not omitted.
 *
 * The counterpart change is in `PLAN_FEATURES`: its "N team members" bullets
 * are gone, because with seats here every card would otherwise state the same
 * fact twice (PRD-13). The mock's feature lists carry no seat bullet either.
 *
 * Caveat worth carrying: `PLAN_TEAM_LIMITS` is headed "enforcement deferred".
 * These numbers are a real promise we do not yet enforce — the same was true
 * when they were feature bullets, so this moves the claim rather than
 * introducing it.
 */
export function getPlanQuotaSummary(plan: PlanKey): string {
  const { domains, prompts } = PLANS[plan]
  const seats = PLAN_TEAM_LIMITS[plan]
  const seatLabel = seats === null ? 'unlimited seats' : `${seats} seat${seats === 1 ? '' : 's'}`
  return `${domains} domain${domains === 1 ? '' : 's'} · ${prompts} prompts · ${seatLabel}`
}

export const TRIAL_DAYS = 7

// ─── Early adopter ───────────────────────────────────────────────────────────

export const EARLY_ADOPTER_LIMIT = 30
export const EARLY_ADOPTER_DISCOUNT = 0.3

export function getDiscountedPrice(price: number): number {
  return Math.round(price * (1 - EARLY_ADOPTER_DISCOUNT))
}

// ─── Polar product ID helpers (server-only) ──────────────────────────────────

const PLAN_TO_PRODUCT: Record<PlanKey, () => string> = {
  starter: () => serverEnv.polarProductStarter,
  growth: () => serverEnv.polarProductGrowth,
  pro: () => serverEnv.polarProductPro,
  agency: () => serverEnv.polarProductAgency,
}

const PLAN_TO_ANNUAL_PRODUCT: Record<PlanKey, () => string> = {
  starter: () => serverEnv.polarProductStarterAnnual,
  growth: () => serverEnv.polarProductGrowthAnnual,
  pro: () => serverEnv.polarProductProAnnual,
  agency: () => serverEnv.polarProductAgencyAnnual,
}

export function getPolarProductId(plan: PlanKey): string {
  return PLAN_TO_PRODUCT[plan]()
}

export function getPolarAnnualProductId(plan: PlanKey): string {
  return PLAN_TO_ANNUAL_PRODUCT[plan]()
}

export const PLAN_ANNUAL_PRICES: Record<PlanKey, number> = {
  starter: 890,
  growth: 2190,
  pro: 4490,
  agency: 6990,
}

export function getPlanForProductId(productId: string): PlanKey | null {
  for (const [plan, getter] of Object.entries(PLAN_TO_PRODUCT)) {
    try {
      if (getter() === productId) return plan as PlanKey
    } catch {
      // env var not set — skip
    }
  }
  // Also check annual product IDs
  for (const [plan, getter] of Object.entries(PLAN_TO_ANNUAL_PRODUCT)) {
    try {
      if (getter() === productId) return plan as PlanKey
    } catch {
      // env var not set — skip
    }
  }
  return null
}

export function getPolarAddonProductId(): string {
  return serverEnv.polarProductAddonPrompts
}

export function isAddonProduct(productId: string): boolean {
  try {
    return productId === serverEnv.polarProductAddonPrompts
  } catch {
    return false
  }
}

export function isEngineAddonProduct(productId: string): boolean {
  try {
    return [
      serverEnv.polarProductAddonEngineStarter,
      serverEnv.polarProductAddonEngineGrowth,
      serverEnv.polarProductAddonEnginePro,
      serverEnv.polarProductAddonEngineAgency,
    ].includes(productId)
  } catch {
    return false
  }
}

// ─── Add-on ─────────────────────────────────────────────────────────────────

export const ADDON_PROMPT_COUNT = 10
export const ADDON_PRICE = 19
export const MAX_ADDONS_PER_PLAN = 1

/**
 * Quota caps that apply to EVERY plan while a subscription is `trialing`,
 * regardless of the plan's own limits. All four plans now trial (checkout
 * route), so a trialing Agency subscription must not get 400 prompts / 10
 * domains for $0 — this is the quota half of the trial. See `entitlementPlan`
 * for the feature-entitlement half, which is intentionally NOT clamped.
 *
 * `dailyFocusPrompts` (R3.15) follows the same logic as `prompts` (owner 2026-09-14: "should have
 * the same logic") — a trialing Agency subscription's scoped-Focus daily collection is capped here,
 * not at `PLANS.agency.dailyFocusPrompts`. `5` sits at or below Starter's own `5`, mirroring how
 * `TRIAL_LIMITS.prompts` (20) sits below Starter's 50.
 */
export const TRIAL_LIMITS = { domains: 1, prompts: 20, dailyFocusPrompts: 5 } as const

/**
 * Effective prompt quota. While `trialing`, EVERY plan (including Agency) is
 * capped at `TRIAL_LIMITS.prompts` regardless of `plan` or `addonActive` — the
 * add-on cannot be purchased during a trial in the current flow, but even if
 * it were, quota during trial must not exceed the trial cap. Use ONLY for a
 * real user's own quota — never for marketing or all-plan comparison
 * displays, which must show each plan's true limits (pass a non-`'trialing'`
 * status, e.g. `undefined`, there).
 */
export function getEffectivePromptLimit(
  plan: PlanKey,
  status: string | null | undefined,
  addonActive: boolean
): number {
  if (status === 'trialing') return TRIAL_LIMITS.prompts
  return PLANS[plan].prompts + (addonActive ? ADDON_PROMPT_COUNT : 0)
}

/**
 * Effective domain quota — the trial-aware counterpart to `getEffectivePromptLimit`.
 * While `trialing`, EVERY plan is capped at `TRIAL_LIMITS.domains`. Use ONLY for
 * a real user's own quota — never for marketing or all-plan comparison displays.
 */
export function getEffectiveDomainLimit(plan: PlanKey, status: string | null | undefined): number {
  if (status === 'trialing') return TRIAL_LIMITS.domains
  return PLANS[plan].domains
}

/**
 * Effective daily Focus-prompt allowance — the twin of `getEffectivePromptLimit` for scoped-Focus
 * daily collection (`docs/tasks/growth-engine-r3.15-prompt-scope-relation/backend-spec.md` §2.7).
 * Pooled per owner account across their projects (see `planDailyFocusCollection`,
 * `src/lib/growth/measure/collection.ts`) exactly as the prompt limit pools.
 *
 * `plan: null` means no entitled subscription — fails closed to `0` (REL-03, PRD-16) rather than
 * defaulting to any paid tier's allowance, checked BEFORE the trial clamp so a caller can never
 * read "no subscription" as "trialing Starter." While `trialing`, EVERY plan (including Agency) is
 * capped at `TRIAL_LIMITS.dailyFocusPrompts` — same trial-quota shape as `getEffectivePromptLimit`.
 */
export function getEffectiveDailyFocusAllowance(
  plan: PlanKey | null,
  status: string | null | undefined
): number {
  if (!plan) return 0
  if (status === 'trialing') return TRIAL_LIMITS.dailyFocusPrompts
  return PLANS[plan].dailyFocusPrompts
}

/**
 * Effective plan for FEATURE-GATE enforcement (`canAccessFeature`). Returns the
 * REAL plan, including while `trialing` — a trialing Agency subscription gets
 * white-label and every other Agency feature, same as a paying Agency
 * subscriber. Quota (prompt/domain limits) is a SEPARATE concern clamped by
 * `getEffectivePromptLimit`/`getEffectiveDomainLimit` via `TRIAL_LIMITS`
 * regardless of plan — do not reintroduce a trial clamp here, or a trialing
 * Agency subscription silently loses the feature it is trialing.
 */
export function entitlementPlan(plan: PlanKey, _status: string | null | undefined): PlanKey {
  return plan
}

// ─── Plan ranking (for upgrade comparisons) ─────────────────────────────────

export const PLAN_RANK: Record<PlanKey, number> = {
  starter: 0,
  growth: 1,
  pro: 2,
  agency: 3,
}

/**
 * Whether moving from `current` to `target` is a downgrade (target ranks lower).
 * Pure `PLAN_RANK` comparison — lives here (client-safe billing primitives) so
 * Client Components can use it WITHOUT pulling in the server-only downgrade gate
 * (`src/lib/billing/downgrade.ts`, which imports the data layer).
 */
export function isDowngrade(current: PlanKey, target: PlanKey): boolean {
  return PLAN_RANK[target] < PLAN_RANK[current]
}

// ─── Team member limits ────────────────────────────────────────────────────
//
// This said "(enforcement deferred)" until 2026-08-12, and it was stale.
// `POST /api/invitations` enforces it: `route.ts:105-120` returns `no_seats`
// (402) once the owner's plan limit is reached, which is also PRD-20's own
// error-code contract.
//
// Corrected because the stale note did real damage rather than none. Asked
// whether these figures could be shown on the pricing screen, I read this
// header, told the user the numbers were "a promise we don't enforce", and
// was wrong — the enforcement was thirty lines away in a route. A comment
// claiming a capability is missing is trusted exactly like a comment claiming
// one exists, and is never checked, because nobody greps to disprove an
// absence.

export const PLAN_TEAM_LIMITS: Record<PlanKey, number | null> = {
  starter: 3,
  growth: 10,
  pro: 25,
  agency: null,
}

/**
 * Nullable-plan-aware accessor for `PLAN_TEAM_LIMITS` — same shape as
 * `getEffectiveDailyFocusAllowance` above. `PLAN_TEAM_LIMITS` is `Record<PlanKey, number | null>`,
 * so indexing it directly demands a non-nullable `PlanKey` — exactly the type pressure that turned
 * `ownerSub?.plan ?? 'starter'` into a Starter seat-limit grant for a non-entitled owner
 * (`POST /api/invitations`, PRD-16). `plan: null` means no entitled subscription — fails closed to
 * `0` seats (REL-03) rather than defaulting to any paid tier's allowance.
 */
export function getPlanTeamLimit(plan: PlanKey | null): number | null {
  if (!plan) return 0
  return PLAN_TEAM_LIMITS[plan]
}

// ─── Feature gates ──────────────────────────────────────────────────────────

type FeatureGates = {
  pdfExport: boolean
  csvExport: boolean
  sharedReports: boolean
  prioritySupport: boolean
  dedicatedSupport: boolean
  dailyScan: boolean
  webhooks: boolean
  directories: boolean
  whiteLabel: boolean
}

export const PLAN_FEATURE_GATES: Record<PlanKey, FeatureGates> = {
  starter: {
    pdfExport: true,
    csvExport: true,
    sharedReports: true,
    prioritySupport: false,
    dedicatedSupport: false,
    dailyScan: true,
    webhooks: true,
    directories: true,
    whiteLabel: false,
  },
  growth: {
    pdfExport: true,
    csvExport: true,
    sharedReports: true,
    prioritySupport: true,
    dedicatedSupport: false,
    dailyScan: true,
    webhooks: true,
    directories: true,
    whiteLabel: false,
  },
  pro: {
    pdfExport: true,
    csvExport: true,
    sharedReports: true,
    prioritySupport: true,
    dedicatedSupport: false,
    dailyScan: true,
    webhooks: true,
    directories: true,
    whiteLabel: false,
  },
  agency: {
    pdfExport: true,
    csvExport: true,
    sharedReports: true,
    prioritySupport: true,
    dedicatedSupport: true,
    dailyScan: true,
    webhooks: true,
    directories: true,
    whiteLabel: true,
  },
}

export type FeatureKey = keyof FeatureGates

export function canAccessFeature(plan: PlanKey | undefined, feature: FeatureKey): boolean {
  if (!plan) return false
  return PLAN_FEATURE_GATES[plan][feature]
}

// ─── Engine add-on helpers (server-only where they access serverEnv) ─────────

export function getEngineAddonProductId(plan: PlanKey): string {
  const map: Record<PlanKey, () => string> = {
    starter: () => serverEnv.polarProductAddonEngineStarter,
    growth: () => serverEnv.polarProductAddonEngineGrowth,
    pro: () => serverEnv.polarProductAddonEnginePro,
    agency: () => serverEnv.polarProductAddonEngineAgency,
  }
  return map[plan]()
}

export function getEngineAddonPrice(plan: PlanKey): number {
  return ENGINE_ADDON_PRICES[plan]
}

export function getEntitledEngines(
  plan: PlanKey | undefined,
  addonEngineActive: boolean
): string[] {
  // NOTE: engines are currently identical across all plans (BASE_ENGINES), so `plan`
  // is not yet used and wrapping it with `entitlementPlan` is a no-op here — but for a
  // DIFFERENT reason than before. `entitlementPlan` no longer clamps trialing⇒Starter
  // (it returns the real plan, so a trialing subscription gets its real plan's
  // features); the no-op today is purely because every plan grants the same engine
  // set. When a plan with a SMALLER engine subset ships (e.g. the Lite tier: ChatGPT +
  // AIO only), this must become plan-aware, and every real-user call site (scan-run.ts,
  // projects/engines/route.ts) will need its OWN entitlement decision for engines —
  // `entitlementPlan` will not provide one, since engine access is a feature, not a
  // trial-clamped quota (see `getEffectivePromptLimit`/`getEffectiveDomainLimit`, which
  // still enforce `TRIAL_LIMITS` regardless of plan).
  const base = [...BASE_ENGINES]
  if (addonEngineActive) {
    base.push(...ADDON_ENGINES)
  }
  return base
}

export function canSelectEngine(
  engine: string,
  plan: PlanKey | undefined,
  addonEngineActive: boolean
): boolean {
  return getEntitledEngines(plan, addonEngineActive).includes(engine)
}
